import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton, cn, Iskelet, Kart, MetinAlani } from "@koclukpanel/ui";
import { useSudoKapisi } from "./SudoKapisi";
import {
  ApiHatasi,
  kurtarmaYordami,
  kurtarmaYordamiKaydet,
  type KurtarmaYordami as Yordam,
  type YordamDurumu,
} from "./api";

/**
 * KURTARMA YORDAMI — F-YED-33/34
 *
 * ================================================================
 * BU BELGE FIRMA SAHIBINE YAZILMISTIR
 * ================================================================
 * Teknik yoneticiye degil: ihtiyac duyuldugu an ona ULASILAMIYOR
 * olabilir (F-TEC-20). Ekranin dili de ona gore — "prod ayaga
 * kaldir" degil, "ne yapilacak, kim yapabilir, ne nerede".
 *
 * ================================================================
 * URETILEN BOLUM DUZENLENEMIYOR
 * ================================================================
 * Hedefler, anahtarlar ve olculen sureler sunucudan geliyor ve
 * ekranda salt okunur. Duzenlenebilseydi kopyasi eskir ve belge,
 * kapanmis bir hedefi hâlâ varmis gibi gosterirdi.
 */

const DURUM_ADI: Record<YordamDurumu, string> = {
  HAZIR: "Hazır",
  EKSIK: "Eksik",
  KULLANILAMAZ: "Kullanılamaz",
};

const DURUM_SINIFI: Record<YordamDurumu, string> = {
  HAZIR: "bg-basarili-500/15 text-onay-700",
  EKSIK: "bg-uyari-500/15 text-uyari-800",
  KULLANILAMAZ: "bg-hata-500/15 text-hata-700",
};

function tarih(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function KurtarmaYordami() {
  const qc = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["kurtarma-yordami"],
    queryFn: kurtarmaYordami,
  });

  const { hataYakala, kapi } = useSudoKapisi();
  const [taslak, setTaslak] = useState<Yordam["elle"] | null>(null);

  // Sunucudan gelen metin, kullanici duzenlemeye baslamadan once
  // forma yansiyor; sonrasinda kullanicinin yazdigi kaybolmasin diye
  // ELLE yonetiliyor.
  useEffect(() => {
    if (data && taslak === null) setTaslak(data.elle);
  }, [data, taslak]);

  const kaydet = useMutation({
    mutationFn: () =>
      kurtarmaYordamiKaydet({
        alanAdiNotu: taslak?.alanAdiNotu ?? "",
        sunucuNotu: taslak?.sunucuNotu ?? "",
        zarfNotu: taslak?.zarfNotu ?? "",
        iletisimNotu: taslak?.iletisimNotu ?? "",
        tatbikatNotu: taslak?.tatbikatNotu ?? "",
      }),
    onSuccess: (y) => {
      setTaslak(y.elle);
      void qc.invalidateQueries({ queryKey: ["kurtarma-yordami"] });
    },
    onError: hataYakala(() => kaydet.mutate()),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-notr-700 text-sm">
          {error instanceof ApiHatasi ? error.message : "Belge alınamadı."}
        </p>
        <div>
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        </div>
      </div>
    );
  }

  const u = data.uretilen;

  return (
    <div className="flex flex-col gap-6">
      {kapi}

      <Kart>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-notr-900 text-sm font-semibold">Kurtarma yordamı</h3>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                DURUM_SINIFI[data.durum],
              )}
            >
              {DURUM_ADI[data.durum]}
            </span>
          </div>
          {/*
            Yazdirma dugmesi: felaket gununde panel de erisilemez
            olabilir. Belgenin kagit bir kopyasi olmasi sartnamenin
            istedigi sey (F-YED-33).
          */}
          <Buton gorunum="ikincil" onClick={() => window.print()}>
            Yazdır
          </Buton>
        </div>

        <p className="text-notr-700 mt-2 text-sm">
          Bu belge <strong>teknik yöneticiye değil firma sahibine</strong> yazılmıştır: ihtiyaç
          duyulduğu an teknik yöneticiye ulaşılamıyor olabilir. Sunucu tamamen kaybedilirse buradaki
          bilgilerle sıfırdan başlanır.
        </p>

        {data.eksikler.length > 0 && (
          <ul className="mt-3 flex flex-col gap-2">
            {data.eksikler.map((e) => (
              <li key={e.kod} className="rounded-alan bg-uyari-500/10 text-notr-700 p-3 text-xs">
                {e.aciklama}
              </li>
            ))}
          </ul>
        )}
      </Kart>

      {/* ---------- ÜRETİLEN ---------- */}
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-notr-900 text-sm font-semibold">Sistemin bildikleri</h3>
          {/*
            Neden duzenlenemedigi YAZIYOR. Yazmasaydik kullanici
            "burayi da doldurayim" diye arardi ve bulamayinca eksik
            sanardi.
          */}
          <p className="text-notr-600 text-xs">
            Bu bölüm her açılışta sistemden yeniden üretilir ve <strong>düzenlenemez</strong> — elle
            yazılan bir kopya, hedeflerden biri kapandığında kendini düzeltmezdi.
          </p>
        </div>

        <Kutu baslik="Yedek hedefleri">
          {u.hedefler.length === 0 ? (
            <p className="text-hata-500 text-sm">Tanımlı hedef yok.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {u.hedefler.map((h) => (
                <li key={h.ad} className="text-sm">
                  <span className="text-notr-900 font-medium">{h.ad}</span>{" "}
                  <span className="text-notr-600 text-xs">
                    ({h.tur}
                    {h.aktif ? "" : ", PASİF"}
                    {h.siteDisi ? ", site dışı" : ", aynı konum"}
                    {h.degismez ? ", değişmez" : ", silinebilir"})
                  </span>
                  {h.konum && <div className="text-notr-600 text-xs">Konum: {h.konum}</div>}
                  <div className="text-notr-600 text-xs">Son yazma: {tarih(h.sonYazma)}</div>
                </li>
              ))}
            </ul>
          )}
        </Kutu>

        <Kutu baslik="Şifreleme anahtarları">
          {/*
            Anahtarin KENDISI degil parmak izi. Belge yazdirilip
            kagida dokulebiliyor; uzerinde anahtar tasiyan bir kagit,
            zarfin varlik sebebini ortadan kaldirirdi.
          */}
          <p className="text-notr-600 mb-2 text-xs">
            Anahtarın kendisi burada değildir; yalnızca parmak izi. Anahtar kurtarma zarfındadır.
          </p>
          <ul className="flex flex-col gap-1">
            {u.anahtarlar.map((a) => (
              <li key={a.etiket} className="text-sm">
                <span className="text-notr-900 font-medium">{a.etiket}</span>{" "}
                <span className="text-notr-600 text-xs">parmak izi {a.parmakIzi}</span>{" "}
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs",
                    a.zarftaMi
                      ? "bg-basarili-500/15 text-onay-700"
                      : "bg-hata-500/15 text-hata-700",
                  )}
                >
                  {a.zarftaMi ? "zarfta" : "ZARFA KONULMADI"}
                </span>
              </li>
            ))}
          </ul>
        </Kutu>

        <Kutu baslik="Ölçülenler">
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Olcum baslik="Son başarılı yedek" deger={tarih(u.sonBasariliYedek)} />
            <Olcum
              baslik="Son tam tatbikat"
              deger={u.sonTatbikat ? tarih(u.sonTatbikat.tarih) : "Hiç yapılmadı"}
            />
            {/*
              "Hedefimiz 4 saat" degil, SON TATBIKATTA olculen. Hedef
              yazsaydik belge bir vaadi olcum diye gosterirdi.
            */}
            <Olcum
              baslik="Ölçülen geri dönüş"
              deger={u.olculenRtoSn === null ? "Ölçülmedi" : `${u.olculenRtoSn} sn`}
            />
            <Olcum baslik="Gecelik iş" deger={u.gecelikIsCron ?? "Tanımsız"} />
          </dl>
        </Kutu>
      </section>

      {/* ---------- ELLE ---------- */}
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-notr-900 text-sm font-semibold">Sistemin bilemeyecekleri</h3>
          <p className="text-notr-600 text-xs">
            Son gözden geçirme: {tarih(taslak?.gozdenGecirildiAt ?? null)} · her{" "}
            {data.elle.gozdenGecirmeAraligiGun} günde bir tekrar bakılmalı.
          </p>
        </div>

        <YordamAlani
          etiket="Alan adı nerede kayıtlı?"
          ipucu="Kayıt firması, hesabın kimin adına olduğu, yenileme tarihi."
          deger={taslak?.alanAdiNotu ?? ""}
          onDegis={(v) => setTaslak((t) => (t ? { ...t, alanAdiNotu: v } : t))}
        />
        <YordamAlani
          etiket="Sunucu nerede, nasıl açılır?"
          ipucu="Sağlayıcı, hesap, ödeme yöntemi, yeni sunucu kurulum adımları."
          deger={taslak?.sunucuNotu ?? ""}
          onDegis={(v) => setTaslak((t) => (t ? { ...t, sunucuNotu: v } : t))}
        />
        <YordamAlani
          etiket="Kurtarma zarfı fiziksel olarak nerede?"
          ipucu="Şifreleme anahtarı olmadan yedekler açılamaz. Zarfa kim ulaşabilir?"
          deger={taslak?.zarfNotu ?? ""}
          onDegis={(v) => setTaslak((t) => (t ? { ...t, zarfNotu: v } : t))}
        />
        <YordamAlani
          etiket="Kime ulaşılacak?"
          ipucu="Teknik yöneticiye ulaşılamadığında sırayla aranacak kişiler."
          deger={taslak?.iletisimNotu ?? ""}
          onDegis={(v) => setTaslak((t) => (t ? { ...t, iletisimNotu: v } : t))}
        />
        <YordamAlani
          etiket="Tatbikat notları" /* F-YED-34 */
          ipucu="Her tatbikatta karşılaşılan aksaklıklar buraya işlenir."
          deger={taslak?.tatbikatNotu ?? ""}
          onDegis={(v) => setTaslak((t) => (t ? { ...t, tatbikatNotu: v } : t))}
          satir={4}
        />

        <div className="flex items-center gap-3">
          <Buton onClick={() => kaydet.mutate()} disabled={kaydet.isPending}>
            {kaydet.isPending ? "Kaydediliyor…" : "Kaydet ve gözden geçirildi işaretle"}
          </Buton>
          {kaydet.isSuccess && (
            <span role="status" className="text-onay-700 text-xs">
              Kaydedildi.
            </span>
          )}
        </div>
        {kaydet.isError && (
          <p className="text-hata-500 text-xs">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
      </section>
    </div>
  );
}

function Kutu({ baslik, children }: { baslik: string; children: React.ReactNode }) {
  return (
    <Kart>
      <h4 className="text-notr-600 mb-2 text-xs font-semibold tracking-wide uppercase">{baslik}</h4>
      {children}
    </Kart>
  );
}

function Olcum({ baslik, deger }: { baslik: string; deger: string }) {
  return (
    <div>
      <dt className="text-notr-600 text-xs">{baslik}</dt>
      <dd className="text-notr-900 text-sm font-medium">{deger}</dd>
    </div>
  );
}

/**
 * Yordam notu alani — ortak `MetinAlani`e INCE bir uyarlayici.
 *
 * Adi bilerek farkli: ice aktarilan bilesenle ayni isimde olsaydi onu
 * GOLGELER ve bu dosya sessizce kendi eski uygulamasini kullanmaya
 * devam ederdi. Uyarlayici duruyor cunku bes cagiran `ipucu/deger/
 * onDegis` arayuzunu kullaniyor.
 */
function YordamAlani({
  etiket,
  ipucu,
  deger,
  onDegis,
  satir = 3,
}: {
  etiket: string;
  ipucu: string;
  deger: string;
  onDegis: (v: string) => void;
  satir?: number;
}) {
  return (
    <MetinAlani
      etiket={etiket}
      yardim={ipucu}
      rows={satir}
      value={deger}
      onChange={(e) => onDegis(e.target.value)}
    />
  );
}
